import React, { useEffect, useState } from "react";
import MetaTags from "react-meta-tags";
import { Link, useParams, useHistory, useLocation } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  CardHeader,
  CardTitle,
  Container,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  Row,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

//Import Breadcrumb
import Breadcrumbs from "../../components/common/Breadcrumb";

//Import Image
import logo from "../../assets/images/logo-sm.png";
import { ApiCore } from "../../helpers/api_helper";
import { AxiosResponse } from "axios";
import { Endpoints } from "../../utils/Endpoints";
import { Loading } from "../../components/common/Loading";
import {
  amountToCurrency,
  formattedDate,
  currentTenant,
  fullUrl,
} from "../../utils/Helpers";
import XModal from "../../components/common/XModal";
import CreateForm from "../../components/forms/CreateForm";
import { EmailFields } from "../../fields/EmailFields";
import { EmailSchema } from "../../schemas/EmailSchema";

const Details = () => {
  const [receipt, setReceipt] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [showEmail, setShowEmail] = useState<boolean>(false);
  const [email, setEmail]= useState<any>({});
  const { id } = useParams();
  const history = useHistory();

  const request = new ApiCore();
  const tenant = currentTenant();

  useEffect(() => {
    request
      .get(Endpoints.receipts + "/" + id)
      .then((response: AxiosResponse) => {
        setReceipt(response.data);
        const emailMessage: any = {
          sender: tenant.name,
          senderemail: "no-reply@eygo.biz",
          subject: "Receipt #" + response.data.id,
          destination: response.data.company.contactemail,
          message: `Dear ${response.data.company.name},\r\nPlease find attached a copy of your receipt.\r\n\r\nIn case you need further assistance or clarification, feel free to contact us`
        }
        setEmail(emailMessage);
        setLoading(false);
      })
      .catch(error => {
        console.error(error);
      });
  }, []);

  const handleOption =(id: string, action: string) => {
    if (action == "edit") {
      handleEdit(id);
    } else if(action == "email") {
      setShowEmail(true);
    } else {
      fetchPdf(id);
    }
  }

  const handleEdit = async (id: string) => {
    history.push("/transactions/receipts/new?id="+id);
  };

  const fetchPdf = (id: string) => {
    toast
    .promise(request
        .get(Endpoints.receipts + '/' + id, { params: { pdf: true } }, "arraybuffer"),{
          pending: "Please wait..",
          success: {
            render({ data }) {
              return "";
            },
          },
          error: {
            render({ data }) {
              return data;
            },
          },
        })
        .then((response: AxiosResponse) => {
          const type = response.headers['content-type']
          const blob = new Blob([response.data], {type: type})
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = `receipt-${+ new Date()}.pdf`
          link.click()
        });
  }

  const handleSendEmail = (values: any) => {
    toast
    .promise(request
        .create(Endpoints.emailReceipt + '/' + receipt.id, values),{
          pending: "Please wait..",
          success: {
            render({ data }) {
              return data ? data.data : 'Email sent successfully';
            },
          },
          error: {
            render({ data }) {
              return data;
            },
          },
        }).then((response: AxiosResponse) => {
          setShowEmail(false);
        });
  }

  return (
    <React.Fragment>
      <ToastContainer />
      <div className="page-content">
        <MetaTags>
          <title>Receipt Detail | Treda</title>
        </MetaTags>
        <Container fluid>
          {/* Render Breadcrumbs */}
          <Breadcrumbs title="Receipts" breadcrumbItem="Receipt Detail" />
          <XModal title={"Send receipt on email"}
                  width={"lg"}
                  open={showEmail}
                  toggle={() => setShowEmail(!showEmail)}
                  onClose={() => setShowEmail(false)}>

            <CreateForm
              fields={EmailFields}
              initialValues={email}
              submitButtonLabel={"Send Email"}
              validationSchema={EmailSchema}
              debug={false}
              handleUpdate={handleSendEmail}
              onClose={() => setShowEmail(false)} />

          </XModal>
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="justify-content-between d-flex align-items-center">
                  <CardTitle className="h4">Receipt</CardTitle>

                  <UncontrolledDropdown className="mt-4 mt-sm-0">
                    <DropdownToggle tag="a" className="btn btn-light">
                      <i className="fa fa-ellipsis-v"></i>
                    </DropdownToggle>

                    <DropdownMenu className="dropdown-menu-end">
                      <DropdownItem
                        to="#"
                        onClick={() => {
                          handleOption(receipt.id, "pdf");
                        }}
                      >
                        PDF Version
                      </DropdownItem>
                      <DropdownItem
                        to="#"
                        onClick={() => {
                          handleOption(receipt.id, "email");
                        }}
                      >
                        Send Via Email
                      </DropdownItem>
                      <DropdownItem
                        to="#"
                        onClick={() => {
                          handleOption(receipt.id, "edit");
                        }}
                      >
                        Edit Receipt
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </CardHeader>
                <CardBody>
                  {loading && <Loading label="Loading receipt. Please wait." />}
                  {!loading && (
                    <>
                      <Row>
                        <Col sm={4}>
                          <div className="receipt-title">
                            <div className="mb-4">
                              <img
                                src={fullUrl(tenant.logo)}
                                alt="logo"
                                height="28"
                              />
                            </div>
                            <div className="text-muted">
                              <p className="mb-1">{tenant.name}</p>
                              <p className="mb-1">
                                <i className="uil uil-envelope-alt me-1"></i>{" "}
                                {tenant.email}
                              </p>
                              <p>
                                <i className="uil uil-phone me-1"></i>{" "}
                                {tenant.phone}
                              </p>
                            </div>
                          </div>
                        </Col>
                        <Col sm={4}>
                          <div className="text-muted">
                            <h5 className="font-size-16 mb-3">Receipt For: </h5>
                            <h5 className="font-size-15 mb-2">
                              {receipt.company.name}
                            </h5>
                            <p className="mb-1">
                              {receipt.company.street} {receipt.company.city}
                            </p>
                            <p className="mb-1">
                              {receipt.company.contactemail}
                            </p>
                            <p>{receipt.company.contractphone}</p>
                          </div>
                        </Col>

                        <Col sm={4}>
                          <div className="text-muted text-sm-end">
                            <div>
                              <h5 className="font-size-15 mb-1">
                                Receipt No:{" "}
                              </h5>
                              <p>#{receipt.id}</p>
                              <div>
                                {receipt.balance == 0 ? (
                                  <span className="badge bg-success font-size-12 ms-2">
                                    PAID
                                  </span>
                                ) : (
                                  <span className="badge bg-danger font-size-12 ms-2">
                                    NOT PAID
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="mt-4">
                              <h5 className="font-size-15 mb-1">
                                Receipt Date:{" "}
                              </h5>
                              <p>{formattedDate(receipt.created)}</p>
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <hr className="my-4" />
                      <div className="py-2">
                        <div className="table-responsive">
                          <table className="table align-middle table-nowrap table-centered mb-0">
                            <thead>
                              <tr>
                                <th style={{ width: "70px" }}>No.</th>
                                <th>Particulars</th>
                                <th
                                  className="text-end"
                                  style={{ width: "120px" }}
                                >
                                  Total ({receipt.currency.code})
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {receipt.items.map((item: any, index: number) => {
                                return (
                                  <tr key={item.id}>
                                    <th scope="row">{index + 1}</th>
                                    <td>
                                      <div>
                                        <h5 className="text-truncate font-size-14 mb-1">
                                          {item.particulars &&
                                          item.particulars.length > 0
                                            ? item.particulars
                                            : item.product
                                            ? item.product.name
                                            : "N/A"}
                                        </h5>
                                      </div>
                                    </td>
                                    <td className="text-end">
                                      {amountToCurrency(item.amount)}
                                    </td>
                                  </tr>
                                );
                              })}
                              <tr>
                                <th
                                  scope="row"
                                  colSpan={2}
                                  className="text-end"
                                >
                                  Total
                                </th>
                                <td className="text-end">
                                  {receipt.currency.code}{" "}
                                  {amountToCurrency(receipt.total)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="d-print-none mt-4">
                          <div className="float-end">
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Details;
